// ═══════════════════════════════════════════════════════════════
//  Signup.jsx — Auto-login after successful signup via AuthContext
// ═══════════════════════════════════════════════════════════════

import React, { useState } from "react";
import { useAuth } from "../../../AuthProvider/AuthContext";
import { Eye, EyeOff } from "lucide-react";

export default function Signup({ onClose, onSwitchToLogin }) {
  const { login } = useAuth();

  const [name,     setName]     = useState("");
  const [email,    setEmail]    = useState("");
  const [phone,    setPhone]    = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [error,    setError]    = useState("");
  const [loading,  setLoading]  = useState(false);
  

  const handleSignup = async () => {
    setError("");
    const normalizedEmail = email.trim();

    if (!name.trim())
      return setError("Full name is required");
    if (!normalizedEmail || !/\S+@\S+\.\S+/.test(normalizedEmail))
      return setError("Enter a valid email address");
    if (phone && !/^\+?[\d\s\-]{7,15}$/.test(phone))
      return setError("Enter a valid phone number");
    if (password.length < 8)
      return setError("Password must be at least 8 characters");

    setLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/signup`, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ name, email: normalizedEmail, phone, password }),
      });
      const data = await res.json();

      if (!res.ok || !data.token || !data.user) {
        setError(data.error || "Signup failed");
      } else {
        // ── Auto-login immediately after signup ─────────────
        login(data.user, data.token);
        onClose();
      }
    } catch {
      setError("Could not connect to server");
    } finally {
      setLoading(false);
    }
  };

  const onKeyDown = (e) => { if (e.key === "Enter") handleSignup(); };

  return (
    <div className="nb-overlay" onClick={onClose}>
      <div
        className="nb-modal bg-light"
        data-aos="zoom-in"
        data-aos-duration="300"
        onClick={(e) => e.stopPropagation()}
      >
        <button className="nb-modal__close" onClick={onClose}>×</button>
        <div className="nb-modal__badge">Free account 🐝</div>
        <h2 className="nb-modal__title">Create your account</h2>
        <p className="nb-modal__sub">Join millions of happy travellers</p>

        {/* Google */}
        <div className="nb-modal__social">
          <button
  type="button"
  className="nb-social-btn"
  onClick={() => {
    window.location.href = `${process.env.REACT_APP_API_URL}/api/auth/google`;
  }}
>
            <svg width="16" height="16" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </button>
        </div>

        <div className="nb-modal__divider"><span>or</span></div>

        {error && <p className="nb-modal__error">{error}</p>}

        <div className="nb-field">
          <label>Full name</label>
          <input
            type="text"
            placeholder="Riya Sharma"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={onKeyDown}
            autoFocus
          />
        </div>
        <div className="nb-field">
          <label>Email address</label>
          <input
            type="email"
            placeholder="riya@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={onKeyDown}
          />
        </div>
        <div className="nb-field">
          <label>Phone number <span style={{ color: "var(--ink-4, #aaa)", fontWeight: 400 }}>(optional)</span></label>
          <input
            type="tel"
            placeholder="+91 98765 43210"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            onKeyDown={onKeyDown}
          />
        </div>
        <div className="nb-field">
  <label>Password</label>

  <div className="password-input-wrap">
    <input
      type={showPass ? "text" : "password"}
      placeholder="Min. 8 characters"
      value={password}
      onChange={(e) => setPassword(e.target.value)}
      onKeyDown={onKeyDown}
    />

    <button
      type="button"
      className="password-toggle"
      onClick={() => setShowPass(prev => !prev)}
      aria-label={showPass ? "Hide password" : "Show password"}
    >
      {showPass ? <EyeOff size={18} /> : <Eye size={18} />}
    </button>
  </div>
</div>

        <button
          className="nb-btn nb-btn--primary nb-btn--full nb-modal__submit"
          onClick={handleSignup}
          disabled={loading}
        >
          {loading ? "Creating account…" : "Create account"}
        </button>

        <p className="nb-modal__footer">
          Already have an account?{" "}
          <span onClick={() => { onClose(); onSwitchToLogin(); }}>Log in</span>
        </p>
      </div>
    </div>
  );
}
